import {Injectable} from '@angular/core';
import {Observable} from "rxjs";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {Snapshot} from "../models/snpashot.model";
import {SummarySettlementsDgSearchResults} from "../models/datagrid-search-results/summary-settlements.dg-search-results.model";
import {SummarySettlement} from "../models/summary-settlements.model";
import {SummarySettlementDtoRequest} from "../models/summary-settlement-dto-request.model";

/**
 * Serwis warstwy danych zarządzający zbiorczymi raportami rozliczeniowymi.
 * Służy do agregacji pojedynczych rozliczeń w większe paczki księgowe,
 * umożliwiając ich przeglądanie w formie ustandaryzowanych tabel DataGrid
 * oraz generowanie nowych podsumowań finansowych.
 */
@Injectable({
  providedIn: 'root'
})
export class SettlementsReportsService implements DataGridServiceInterface<Observable<SummarySettlementsDgSearchResults>> {

  constructor(
    private apiService: ApiService,
    public commonUtils : CommonUtils,
  ) {
  }

  /**
   * Pobiera paginowaną listę zbiorczych raportów rozliczeniowych.
   *
   * @param limit Maksymalna liczba rekordów na stronę.
   * @param offset Przesunięcie wyników.
   * @param sortOrder Kierunek sortowania (np. 'ASC', 'DESC').
   * @param orderBy Kolumna sortująca.
   * @param showDeletedObjects Flaga widoczności anulowanych/usuniętych raportów.
   * @returns Observable emitujący ustandaryzowany obiekt wyników dla tabeli DataGrid.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<SummarySettlementsDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/settlements-report/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new SummarySettlementsDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new SummarySettlement(), [],
          vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wyszukuje zbiorcze raporty finansowe w oparciu o filtry (np. okres rozliczeniowy, kwota łączna).
   *
   * @param limit Rozmiar strony.
   * @param offset Przesunięcie.
   * @param searchFieldsData Mapa zawierająca zdefiniowane filtry.
   * @param sortOrder Kierunek sortowania.
   * @param orderBy Kolumna sortująca.
   * @param showDeletedObjects Flaga widoczności usuniętych rekordów.
   * @returns Observable emitujący przefiltrowaną listę raportów.
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>,
                   sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<SummarySettlementsDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>(`/dashboard/settlements-report/search`, data).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new SummarySettlementsDgSearchResults(vetineoApiResponse.response.total,
          this.commonUtils.bestTypedArrayCopyEver(new SummarySettlement(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera historię zmian (logi audytowe) dla konkretnego raportu zbiorczego.
   *
   * @param uuid Unikalny identyfikator raportu zbiorczego.
   * @returns Observable emitujący tablicę zdarzeń audytowych (Snapshot).
   */
  showChanges(uuid: string): Observable<Snapshot[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/settlements-report/changes/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new Snapshot(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Inicjuje proces generowania nowego, zbiorczego raportu rozliczeniowego
   * na podstawie przekazanych parametrów żądania (DTO).
   *
   * @param summarySettlementDtoRequest Obiekt DTO (Data Transfer Object) zawierający parametry wejściowe do wygenerowania raportu (np. zakres dat, ID paczek rozliczeniowych).
   * @returns Observable emitujący wygenerowany i zapisany obiekt zbiorczego raportu (SummarySettlement).
   */
  prepareNewSummarySettlement(summarySettlementDtoRequest: SummarySettlementDtoRequest): Observable<SummarySettlement> {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/settlements-report/process`, summarySettlementDtoRequest).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<SummarySettlement>(new SummarySettlement(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

}
